
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LeadStatus } from '@/types/lead';
import { Progress } from '@/components/ui/progress';
import { ArrowUpRight, ArrowDownRight, CheckCircle2, XCircle, BarChart2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface LeadSuccessMetricsCardProps {
  leadStatus: LeadStatus;
  timeInStatus?: number; // in days
  companySuccessRate?: number; // percentage
  industrySuccessRate?: number; // percentage
  isLoading?: boolean;
  className?: string;
}

const LeadSuccessMetricsCard: React.FC<LeadSuccessMetricsCardProps> = ({
  leadStatus,
  timeInStatus = 0,
  companySuccessRate = 0,
  industrySuccessRate = 0,
  isLoading = false,
  className,
}) => {
  // Determine success probability based on status
  const getSuccessProbability = (status: LeadStatus): number => {
    switch (status) {
      case 'new': return 10;
      case 'contacted': return 25;
      case 'qualified': return 40;
      case 'proposal': return 60;
      case 'negotiation': return 75;
      case 'closed_won': return 100;
      case 'closed_lost': return 0;
      default: return 0;
    }
  };

  const successProbability = getSuccessProbability(leadStatus);
  const isWon = leadStatus === 'closed_won';
  const isLost = leadStatus === 'closed_lost';
  const isActive = !isWon && !isLost;
  
  // Calculate if company is performing better than industry average
  const companyVsIndustry = companySuccessRate - industrySuccessRate;
  const isPerformingBetter = companyVsIndustry > 0;

  return (
    <Card className={className}>
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-xl font-semibold">Success Metrics</CardTitle>
        <CardDescription>
          Tracking lead performance and success indicators
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-2 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
          </>
        ) : (
          <>
            {isActive && (
              <>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Success Probability</span>
                    <span className="font-medium">{successProbability}%</span>
                  </div>
                  <Progress value={successProbability} className="h-2" />
                </div>
                
                <div className="flex justify-between text-sm">
                  <span>Time in Current Status</span>
                  <span className="font-medium">{timeInStatus.toFixed(1)} days</span>
                </div>
              </>
            )}
            
            {isWon && (
              <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-md">
                <CheckCircle2 className="text-green-500 h-8 w-8 mr-3" />
                <div>
                  <p className="font-medium">Deal Won</p>
                  <p className="text-sm text-muted-foreground">
                    This lead has been successfully converted
                  </p>
                </div>
              </div>
            )}
            
            {isLost && (
              <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 rounded-md">
                <XCircle className="text-red-500 h-8 w-8 mr-3" />
                <div>
                  <p className="font-medium">Deal Lost</p>
                  <p className="text-sm text-muted-foreground">
                    This lead was unsuccessful
                  </p>
                </div>
              </div>
            )}
            
            {!isLoading && companySuccessRate > 0 && (
              <div className="pt-2 border-t">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm">Company Success Rate</span>
                  <div className="flex items-center">
                    <span className="font-medium">{companySuccessRate.toFixed(1)}%</span>
                    {isPerformingBetter ? (
                      <ArrowUpRight className="text-green-500 h-4 w-4 ml-1" />
                    ) : (
                      <ArrowDownRight className="text-red-500 h-4 w-4 ml-1" />
                    )}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground flex justify-between">
                  <span>Industry Avg: {industrySuccessRate.toFixed(1)}%</span>
                  <span className={isPerformingBetter ? "text-green-500" : "text-red-500"}>
                    {Math.abs(companyVsIndustry).toFixed(1)}% {isPerformingBetter ? 'better' : 'worse'}
                  </span>
                </div>
              </div>
            )}
            
            {!isLoading && companySuccessRate === 0 && (
              <div className="flex items-center p-3 bg-gray-50 dark:bg-gray-800/30 rounded-md">
                <BarChart2 className="text-gray-400 h-5 w-5 mr-2" />
                <p className="text-sm text-muted-foreground">
                  Not enough data to calculate success rates
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadSuccessMetricsCard;
